import { Injectable } from '@angular/core';
import { ObjectBreadcrumb } from 'app/component-library/object-container/object-breadcrumbs/object-breadcrumb.model';
import { DrawerItemSubtitle } from 'app/component-library/object-drawer/drawer-item-subtitle';
import { ObjectDrawerFilter } from 'app/component-library/object-drawer/object-drawer-filter/object-drawer-filter';
import { ObjectDrawerParameters } from 'app/component-library/object-drawer/object-drawer-parameters';
import { SectionContainerConfig } from 'app/component-library/section-container/section-container-config';
import { SectionContainerProviderService } from 'app/component-library/section-container/section-container-provider-service';
import { BackendPaginationService } from 'app/core/backend-pagination.service';
import { BackendService } from 'app/core/backend.service';
import { CONFIG } from 'app/core/config';
import { HierarchyStructureInfoProviderService, TreeviewResponseValues } from 'app/hierarchy/hierarchy-details/overview/hierarchy-structure-info-provider.service';
import { MitigationTile } from 'app/viewmodels/analysis/mitigation-tile';
import { MitigationTreeOption } from 'app/viewmodels/options/mitigation-tree-option';
import { TreeNode } from 'primeng/api';
import { Observable, Subject, Subscription } from 'rxjs';
import { RecommendationsService } from './recommendations.service';

@Injectable({
  providedIn: 'root',
})
export class MitigationsProviderService implements SectionContainerProviderService {
  public breadcrumbChangedSub: Subscription = undefined;
  public breadcrumbRawData = new Subject<TreeviewResponseValues[]>();
  public selectedItemName: string = null;

  constructor(
    private backendPaginationService: BackendPaginationService,
    private backendService: BackendService,
    private hierarchyInfoProvider: HierarchyStructureInfoProviderService,
    private recommendationsService: RecommendationsService,
  ) { }

  public getConfig(): SectionContainerConfig {
    const config = new SectionContainerConfig();

    config.itemSubtitles = [new DrawerItemSubtitle('siteName'), new DrawerItemSubtitle('modelName')];
    config.itemIdField = 'orgTreeNodeId';
    config.itemTitleField = 'name';
    config.title = 'Mitigations';
    config.itemTypeName = 'hierarchy';
    config.enableAdd = false;
    config.hasFilters = true;
    config.showTreeView = true;
    config.outputObject = true;
    config.showNavigationButtons = true;

    return config;
  }

  getFilteredTreeNodes(params: ObjectDrawerParameters): Observable<TreeNode[]> {
    return this.backendService.postObjectListRoute<MitigationTreeOption>(
      MitigationTreeOption,
      params,
      CONFIG.apiRoutes.objectDrawerMitigationFilteredTreeNodes,
      false,
    );
  }

  getTreeRootData(params: ObjectDrawerParameters): Observable<TreeNode[]> {
    return this.backendService.postObjectListRoute<MitigationTreeOption>(
      MitigationTreeOption,
      params,
      CONFIG.apiRoutes.objectDrawerMitigationTreeRoot,
      false,
    );
  }

  getTreeChildData(params: ObjectDrawerParameters): Observable<TreeNode[]> {
    return this.backendService.postObjectListRoute<MitigationTreeOption>(
      MitigationTreeOption,
      params,
      CONFIG.apiRoutes.objectDrawerMitigationTreeChildren,
      false,
    );
  }

  getData(first: number, pageSize: number, params: ObjectDrawerParameters): Observable<any> {
    return this.backendPaginationService.paginateWithParameters<MitigationTile>(MitigationTile, params, first, pageSize, false);
  }

  getTotalItemCount(params: ObjectDrawerParameters): Observable<any> {
    return this.backendPaginationService.getCountWithParameters<MitigationTile>(MitigationTile, params, false);
  }

  public getBreadcrumbTrail(): Subject<ObjectBreadcrumb[]> {
    const breadcrumbObserve = new Subject<ObjectBreadcrumb[]>();

    this.breadcrumbChangedSub = this.recommendationsService.orgTreeNodeChanged.subscribe(orgTreeNodeId => {
      this.hierarchyInfoProvider.obtainTreeviewItems(orgTreeNodeId, CONFIG.apiRoutes.objectDrawerMitigationHierarchyTreeChildren).subscribe(
        data => {
          if (!data) {
            breadcrumbObserve.next(null);

            return;
          }

          this.selectedItemName = data.selectedItem.value;
          const selectedIds = new Set<string>();
          data.values.map(node => {
            node.value.forEach(r => {
              const treeOpt = new MitigationTreeOption();
              treeOpt.loadFromJson(r as MitigationTreeOption);
              if (treeOpt.selectable) {
                selectedIds.add(treeOpt.data);
              }
            });
          });
          this.breadcrumbRawData.next(data.values);

          const breadcrumbs = [] as ObjectBreadcrumb[];

          data.values.reverse()
            .forEach((node, i) => {
              const parent = node.key;
              const breadcrumb = new ObjectBreadcrumb();

              breadcrumb.label = parent.value;
              breadcrumb.url = selectedIds.has(parent.key) ? `${CONFIG.routes.hierarchy.parent}/${parent.key}` : null; // first is site, not hierarchy item
              breadcrumb.itemId = parent.key;
              breadcrumbs.push(breadcrumb);
            });
          const selectedBreadcrumb = new ObjectBreadcrumb();

          selectedBreadcrumb.label = data.selectedItem.value;
          breadcrumbs.push(selectedBreadcrumb);
          breadcrumbObserve.next(breadcrumbs);
        },
        () => {
          breadcrumbObserve.next(null);
        },
      );
    });

    return breadcrumbObserve;
  }

  getObjectDrawerFilters(): Observable<any> {
    return this.backendService.getAllRoute<ObjectDrawerFilter>(ObjectDrawerFilter, CONFIG.apiRoutes.objectDrawerFilterMitigation);
  }

  onBreadcrumbsDestroy(): void {
    if (this.breadcrumbChangedSub) {
      this.breadcrumbChangedSub.unsubscribe();
    }
  }
}
